import { useEffect, useState } from "react";
import { AdditionalNoteEntries } from "@/components/AdditionalNoteEntries";
import { formatTimestamp } from "@/utils/datetime";

const visibilityOptions = ["personal", "company", "selected_people"];

const createEditForm = (item) => ({
  note: item.original_note || item.notes || item.title || "",
  original_note_changed: false,
  project_id: item.project_id || "",
  visibility: item.visibility || "company",
  visible_to_user_ids: item.visible_to_user_ids || [],
});

export const MobileCommunicationDrawer = ({
  canManageCommunications,
  item,
  onClose,
  onAddAdditionalNote,
  onComplete,
  onConvertToTask,
  onSave,
  onUpdateAdditionalNote,
  projectLabel,
  projects,
  users,
  visibilityLabel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editForm, setEditForm] = useState(() => createEditForm(item));
  const [peopleSearch, setPeopleSearch] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const canEdit = (
    !item.deleted_at
    && item.status === "open"
    && item.visibility_version === "stage2"
    && item.can_edit_original_note
  );

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const matchingProjects = projects.filter((project) => (
    project.lifecycle_state !== "closed"
    && `${project.project_code} ${project.name}`
      .toLowerCase()
      .includes(projectSearch.toLowerCase())
  ));

  const matchingPeople = users.filter((person) => (
    person.name.toLowerCase().includes(peopleSearch.toLowerCase())
  ));

  const openEditor = () => {
    setEditForm(createEditForm(item));
    setProjectSearch(item.project_id ? projectLabel(item.project_id) : "");
    setIsEditing(true);
  };

  const togglePerson = (userId) => {
    const selectedIds = editForm.visible_to_user_ids.includes(userId)
      ? editForm.visible_to_user_ids.filter((id) => id !== userId)
      : [...editForm.visible_to_user_ids, userId];
    setEditForm({ ...editForm, visible_to_user_ids: selectedIds });
  };

  const save = async () => {
    setIsSubmitting(true);
    const updated = await onSave(item.id, editForm);
    setIsSubmitting(false);
    if (updated) {
      setIsEditing(false);
      setPeopleSearch("");
      setProjectSearch("");
    }
  };

  const complete = async () => {
    setIsSubmitting(true);
    const completed = await onComplete(item.id);
    setIsSubmitting(false);
    if (completed) {
      onClose();
    }
  };

  const convertToTask = async () => {
    setIsSubmitting(true);
    const converted = await onConvertToTask(item.id);
    setIsSubmitting(false);
    if (converted) {
      onClose();
    }
  };
  const isHistory = item.status === "complete" || Boolean(item.deleted_at);

  return (
    <div
      className="mobile-communication-drawer-backdrop"
      data-testid={`mobile-communication-drawer-${item.id}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      role="presentation"
    >
      <aside
        aria-labelledby={`mobile-communication-drawer-title-${item.id}`}
        aria-modal="true"
        className="mobile-communication-drawer"
        role="dialog"
      >
        <header>
          <div>
            <span data-testid={`mobile-communication-status-${item.id}`}>
              {isHistory
                ? "Completed History"
                : item.item_type === "task"
                  ? "Task"
                  : "Open"}
            </span>
            <h2 data-testid={`mobile-communication-drawer-title-${item.id}`}>
              {projectLabel(item.project_id) === "No project link"
                ? "Communication"
                : projectLabel(item.project_id)}
            </h2>
          </div>
          <button
            aria-label="Close communication details"
            data-testid={`mobile-communication-close-${item.id}`}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </header>
        {isEditing ? (
          <div className="mobile-communication-editor">
            <label htmlFor={`mobile-communication-original-note-input-${item.id}`}>
              Original Note
            </label>
            <textarea
              id={`mobile-communication-original-note-input-${item.id}`}
              data-testid={`mobile-communication-original-note-input-${item.id}`}
              onChange={(event) => setEditForm({
                ...editForm,
                note: event.target.value,
                original_note_changed: true,
              })}
              value={editForm.note}
            />
            <label htmlFor={`mobile-communication-project-${item.id}`}>Project</label>
            <input
              id={`mobile-communication-project-${item.id}`}
              data-testid={`mobile-communication-project-search-${item.id}`}
              onChange={(event) => {
                setProjectSearch(event.target.value);
                if (editForm.project_id) {
                  setEditForm({ ...editForm, project_id: "" });
                }
              }}
              placeholder="Search project..."
              value={projectSearch}
            />
            {projectSearch && !editForm.project_id && (
              <div data-testid={`mobile-communication-project-options-${item.id}`}>
                {matchingProjects.map((project) => (
                  <button
                    data-testid={`mobile-communication-project-option-${item.id}-${project.id}`}
                    key={project.id}
                    onClick={() => {
                      setEditForm({ ...editForm, project_id: project.id });
                      setProjectSearch(`${project.project_code} — ${project.name}`);
                    }}
                    type="button"
                  >
                    {project.project_code} — {project.name}
                  </button>
                ))}
              </div>
            )}
            {editForm.project_id && (
              <button
                data-testid={`mobile-communication-project-clear-${item.id}`}
                onClick={() => {
                  setEditForm({ ...editForm, project_id: "" });
                  setProjectSearch("");
                }}
                type="button"
              >
                Clear project
              </button>
            )}
            <div
              aria-label="Communication visibility"
              className="mobile-communication-visibility"
              data-testid={`mobile-communication-visibility-${item.id}`}
            >
              {visibilityOptions.map((visibility) => (
                <button
                  aria-pressed={editForm.visibility === visibility}
                  className={editForm.visibility === visibility ? "active" : ""}
                  data-testid={`mobile-communication-visibility-${item.id}-${visibility}`}
                  key={visibility}
                  onClick={() => setEditForm({
                    ...editForm,
                    visibility,
                    visible_to_user_ids: visibility === "selected_people"
                      ? editForm.visible_to_user_ids
                      : [],
                  })}
                  type="button"
                >
                  {visibility.replaceAll("_", " ")}
                </button>
              ))}
            </div>
            {editForm.visibility === "selected_people" && (
              <div data-testid={`mobile-communication-selected-people-${item.id}`}>
                <input
                  data-testid={`mobile-communication-people-search-${item.id}`}
                  onChange={(event) => setPeopleSearch(event.target.value)}
                  placeholder="Search people..."
                  value={peopleSearch}
                />
                <div className="mobile-communication-people-list">
                  {matchingPeople.map((person) => (
                    <label key={person.id}>
                      <input
                        checked={editForm.visible_to_user_ids.includes(person.id)}
                        data-testid={`mobile-communication-person-${item.id}-${person.id}`}
                        onChange={() => togglePerson(person.id)}
                        type="checkbox"
                      />
                      {person.name} — {person.role}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mobile-communication-details">
            <p data-testid={`mobile-communication-note-display-${item.id}`}>
              {item.original_note || item.notes || item.title}
            </p>
            <AdditionalNoteEntries
              disabled={isHistory}
              item={item}
              onAdd={onAddAdditionalNote}
              onUpdate={onUpdateAdditionalNote}
            />
            <dl>
              <div>
                <dt>Created by</dt>
                <dd data-testid={`mobile-communication-author-${item.id}`}>
                  {item.created_by_name_snapshot || "Unknown"}
                </dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd data-testid={`mobile-communication-created-at-${item.id}`}>
                  {item.created_at ? formatTimestamp(item.created_at) : "Unknown"}
                </dd>
              </div>
              <div>
                <dt>Visibility</dt>
                <dd data-testid={`mobile-communication-visibility-display-${item.id}`}>
                  {visibilityLabel(item)}
                </dd>
              </div>
              {item.deleted_at && (
                <div>
                  <dt>Legacy archive metadata</dt>
                  <dd data-testid={`mobile-communication-archived-at-${item.id}`}>
                    {formatTimestamp(item.deleted_at)}
                    {item.deleted_by_name_snapshot
                      ? ` · ${item.deleted_by_name_snapshot}`
                      : ""}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
        <footer>
          {isEditing ? (
            <>
              <button
                data-testid={`mobile-communication-save-${item.id}`}
                disabled={isSubmitting}
                onClick={save}
                type="button"
              >
                {isSubmitting ? "Saving…" : "Save"}
              </button>
              <button
                data-testid={`mobile-communication-cancel-${item.id}`}
                disabled={isSubmitting}
                onClick={() => setIsEditing(false)}
                type="button"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button
                  data-testid={`mobile-communication-edit-${item.id}`}
                  disabled={isSubmitting}
                  onClick={openEditor}
                  type="button"
                >
                  Edit
                </button>
              )}
              {canManageCommunications && !isHistory && item.item_type === "note" && (
                <button
                  data-testid={`mobile-communication-convert-task-${item.id}`}
                  disabled={isSubmitting}
                  onClick={convertToTask}
                  type="button"
                >
                  Make Task
                </button>
              )}
              {canManageCommunications && !isHistory && (
                <button
                  data-testid={`mobile-communication-complete-${item.id}`}
                  disabled={isSubmitting}
                  onClick={complete}
                  type="button"
                >
                  Complete
                </button>
              )}
            </>
          )}
        </footer>
      </aside>
    </div>
  );
};